/**
 * Image Utilities
 * Helper functions for processing images in the browser.
 */

/**
 * Longest edge, in pixels, that a logo is downscaled to before being embedded
 * in a PDF.
 *
 * Every PDF that carries a logo draws it into a 48x48 point box. A PDF point is
 * 1/72 inch, so 48pt is two thirds of an inch — 200px at 300 DPI, which is the
 * resolution a commercial printer would ask for. 256 gives that a comfortable
 * margin and is still ~1/20th the weight of the average logo customers upload.
 */
export const LOGO_MAX_PX = 256;

/**
 * Scales `width` x `height` down to fit inside a `max` x `max` box, preserving
 * aspect ratio. Never scales up — an image already smaller than the box is left
 * alone, so a small logo is not blown up into a blurry one.
 *
 * Split out from the DOM work so the arithmetic can be tested directly.
 */
export function fitWithin(
    width: number,
    height: number,
    max: number,
): { width: number; height: number } {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return { width: 0, height: 0 };
    }
    const longest = Math.max(width, height);
    if (longest <= max) return { width: Math.round(width), height: Math.round(height) };

    const scale = max / longest;
    // At least 1px on the short edge, so an extreme banner ratio cannot round to zero.
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

/**
 * Converts an image URL to a Base64 Data URL.
 * Bypasses CORS issues by performing a manual fetch instead of direct assignment.
 *
 * `maxDimension` downscales the image before encoding. This matters more than it
 * looks: @react-pdf/renderer embeds the data URL verbatim, base64 adds ~33% on
 * top, and the result is written into EVERY generated document. Invoice PDFs
 * were averaging 760 KB each against ~10 KB of actual invoice content, because
 * a full-resolution logo upload was being carried into a 48pt box. Storage is
 * the fastest-growing thing in the product, so this is the difference between
 * roughly 450 GB and 30 GB a year at a thousand customers issuing 50 invoices a
 * month.
 *
 * Downscaling is best-effort. If anything in the canvas path fails — an SVG with
 * no intrinsic size, a decode error, a browser without canvas — the original
 * data URL is returned rather than losing the logo. A heavy PDF beats a PDF with
 * the customer's branding missing.
 */
export async function urlToBase64(
    url: string,
    options?: { maxDimension?: number },
): Promise<string | null> {
    if (!url) return null;

    try {
        const response = await fetch(url, {
            mode: 'cors',
            cache: 'no-cache',
        });

        if (!response.ok) {
            console.error(`Failed to fetch image for base64 conversion: ${response.statusText}`);
            return null;
        }

        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        if (!dataUrl) return null;

        const max = options?.maxDimension;
        if (!max) return dataUrl;

        return (await downscaleDataUrl(dataUrl, max)) ?? dataUrl;
    } catch (err) {
        console.error('Error converting image URL to Base64:', err);
        return null;
    }
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => {
            console.error('FileReader error during image conversion');
            resolve(null);
        };
        reader.readAsDataURL(blob);
    });
}

/**
 * Redraws a data URL at no more than `max` pixels on its longest edge.
 *
 * Returns null — meaning "keep the original" — rather than throwing, on any
 * failure. Output is always PNG: logos are overwhelmingly flat-colour with
 * transparency, which PNG keeps and JPEG would both fill with black and blur
 * around the edges.
 */
async function downscaleDataUrl(dataUrl: string, max: number): Promise<string | null> {
    try {
        const img = await loadImage(dataUrl);
        const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, max);
        if (!width || !height) return null;

        // Already within budget: re-encoding would only lose quality for nothing.
        if (img.naturalWidth <= max && img.naturalHeight <= max) return null;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const out = canvas.toDataURL('image/png');
        // A tainted canvas throws above; a failed encode can still come back short.
        return out && out.length > 'data:image/png;base64,'.length ? out : null;
    } catch {
        return null;
    }
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image decode failed'));
        img.src = src;
    });
}
